/**
 * @module helpers/number-helper
 */

export function precisionRound(num, precision, type = 'round') {
  const factor = 10 ** precision;
  if (type === 'round') {
    return Math.round((num + Number.EPSILON) * factor) / factor;
  }
  if (type === 'ceil') {
    return Math.ceil((num + Number.EPSILON) * factor) / factor;
  }
  if (type === 'floor') {
    return Math.floor((num + Number.EPSILON) * factor) / factor;
  }
  return num;
}

/**
 * Delimit number
 *
 * @example
 * delimiter(1000000)
 * // => '1.000.000'
 *
 * @param  {Number|String} val Number to delimit
 * @return {String} Delimited number
 */
export function delimiter(val, precision = 0) {
  let str = `${val}`; // convert to string
  if (precision > 0) {
    str = `${precisionRound(Number(val), precision)}`;
  }
  const x = str.split('.');
  const delimited = x[0].replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  let result = `${val < 0 ? '-' : ''}${delimited}`;
  if (precision > 0 && x[1]) {
    result += `,${x[1]}`;
  }
  return result;
}

/**
 * Convert number to money
 *
 * @example
 * money(1000000)
 * // => 'Rp1.000.000'
 *
 * @param  {Number|String} val Number to convert
 * @return {String} Converted number in money format
 */
export function money(val, precision = 0) {
  const int = delimiter(Number(val) || 0, precision);
  if (int.charAt(0) === '-') {
    return int.replace(/^-/g, '-Rp');
  }
  return `Rp${int}`;
}

/**
 * Transform number to simple format
 * can't satisfy these cases below
 * 15.750 ---> 15,7rb
 * 13.456.000 ---> 13,4jt
 *
 * @example
 * simpleNumber(500000)
 * // => '500rb'
 * simpleNumber(500000, ['', 'k', 'M', 'B', 'T'])
 * // => '500k'
 *
 * @param {Number} amount
 * @param {Array} units
 * @return {String} Tranformed number
 */
export function simpleNumber(amount, units = ['', 'rb', 'jt', 'M', 'T']) {
  let parsedNumber = Math.abs(amount) || 0;
  let unitIndex = 0;
  const unitsComplete = [...units, ...['', 'rb', 'jt', 'M', 'T'].slice(units.length)];

  while (parsedNumber >= 1000) {
    parsedNumber /= 1000;
    unitIndex += 1;
  }

  // Get the final number with a max of one decimal place only
  parsedNumber = parseFloat(Math.floor(parsedNumber * 10) / 10);

  const simpleFormat = `${parsedNumber.toString().replace('.', ',')}${unitsComplete[unitIndex]}`;
  return `${amount < 0 ? '-' : ''}${simpleFormat}`;
}

/**
 * Transform number to simple currency format
 * can't satisfy these cases below
 * 15.750 ---> 15,7rb
 * 13.456.000 ---> 13,4jt
 *
 * @example
 * simpleMoney(500000)
 * // => 'Rp500rb'
 *
 * @param  {Number} Amount required e.g. 10000
 * @param  {String} Symbol optional e.g. 'Rp'
 * @return {String} Transformed number
 */
export function simpleMoney(amount, symbol = 'Rp') {
  const simpleFormat = `${symbol}${simpleNumber(amount).replace(/^-/, '')}`;
  return `${amount < 0 ? '-' : ''}${simpleFormat}`;
}

export function round(val, precision = 2) {
  const factor = 10 ** precision;
  return Math.round((val + 0.00001) * factor) / factor;
}

export default {
  delimiter,
  money,
  simpleMoney,
  simpleNumber,
  round,
};
